import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Menu, ChevronDown, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_54396c88-8cf9-4315-926b-d20a7ab432d7/artifacts/hhkc1a2d_kesult_logo_webp.webp";

export const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const basePath = language === 'en' ? '/en' : '';

  const navLinks = [
    { href: `${basePath}/`, label: t('nav.home') },
    { href: language === 'en' ? '/en/how-i-work' : '/werkwijze', label: t('nav.werkwijze') },
    { href: language === 'en' ? '/en/services' : '/diensten', label: t('nav.diensten') },
    { href: language === 'en' ? '/en/seo-packages' : '/seo-pakketten', label: language === 'nl' ? 'SEO Pakketten' : 'SEO Packages' },
    { href: `${basePath}/seo-consultant`, label: language === 'nl' ? 'SEO specialist inhuren' : 'Hire Dutch SEO Specialist' },
    { href: language === 'en' ? '/en/about' : '/over-mij', label: t('nav.overMij') },
  ];

  const isActive = (href) => {
    const currentPath = location.pathname;
    if (href === basePath + '/' || href === '/') {
      return currentPath === href || currentPath === basePath || currentPath === '/';
    }
    return currentPath.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={`${basePath}/`} 
            className="flex items-center flex-shrink-0"
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL}
              alt="Kesult SEO Consulting"
              className="h-9 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href) 
                    ? 'text-[#4286F4]' 
                    : 'text-[#555] hover:text-[#08080A]'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="text-xs font-medium text-[#888] hover:text-[#333] px-2"
                  data-testid="language-switcher"
                >
                  {language.toUpperCase()}
                  <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl bg-white shadow-lg border border-gray-100">
                <DropdownMenuItem 
                  onClick={() => setLanguage('nl')}
                  className={`font-medium text-sm ${language === 'nl' ? 'bg-gray-50 text-[#4286F4]' : ''}`}
                  data-testid="lang-nl"
                >
                  NL - Nederlands
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setLanguage('en')}
                  className={`font-medium text-sm ${language === 'en' ? 'bg-gray-50 text-[#4286F4]' : ''}`}
                  data-testid="lang-en"
                >
                  EN - English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button */}
            <Link to={`${basePath}/contact`} className="hidden md:block">
              <button 
                className="bg-[#4286F4] text-white font-medium px-5 py-2 rounded-full text-sm hover:bg-[#3070d4] transition-colors duration-200"
                data-testid="header-cta"
              >
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
              </button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2 text-[#333]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-trigger"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 text-base font-medium rounded-lg ${
                  isActive(link.href) 
                    ? 'text-[#4286F4] bg-blue-50' 
                    : 'text-[#333] hover:bg-gray-50'
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
            <Link 
              to={`${basePath}/contact`}
              onClick={() => setMobileOpen(false)}
              className="mt-4"
            >
              <button className="w-full bg-[#4286F4] text-white font-semibold py-3 rounded-full hover:bg-[#3070d4] transition-colors">
                {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
              </button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
